import React, { useEffect } from "react";
import KanbanCategory from "./KanbanCategory/KanbanCategory";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useDispatch, useSelector } from "react-redux";
import { setMilestones, setBrands } from "actions/user";
import { useHttp } from "hooks/useHttp";
import addNotification from "helpers/notify";
import { updateKanban } from "api/milestones/editMilestone";
import { updatePostStatus, updateAllPostsStatus } from "actions/posts";
import { editPost } from "api/brands/editPost";
import { setIdeas } from "actions/idea";
import { updateIdea } from "api/ideas/addUpdateIdea";
import { deriveQueueStatus } from "helpers/deriveQueueStatus";

const colors = [
  "#FFA500",
  "#E9367E",
  "#3E6EDF",
  "#8B5AEE",
  "#6FCAFF",
  "#FFA500",
  "#E9367E",
  "#3E6EDF",
  "#8B5AEE",
  "#6FCAFF",
];

const KanbanBoard = ({
  posts,
  statuses,
  setModal,
  setBrandToDelete,
  setBrandToUpdate,
  setBrandListToEdit,
  source='post',
  onEdit,
  onArchive,
  isAdmin,
}) => {
  const dispatch = useDispatch();

  const brandsRedux = useSelector((s) => s.user.brands);

  // Map the milestone column's `key` to the worker pipeline's queueStatus enum.
  // Keys we care about (case-insensitive substrings):
  //   awaiting/await    → 'awaiting-approval' (un-approve back to the queue)
  //   approved/approve  → 'approved'   (triggers POST_APPROVED / IDEA_POSTED outbox)
  //   reject/declined   → 'rejected'
  //   pending/draft     → 'pending'
  //   posted/published  → 'posted'
  // Anything else: pass the raw key — backend ignores unknown queueStatus values.
  // Implementation lives in helpers/deriveQueueStatus so it can be unit-tested.

  function onDragEnd(res) {
    console.log(res);
    if (source === 'post') {
      if (res?.type === "column") {
        const data = {
          statusId: res?.draggableId,
          sourceIndex: res?.source?.index,
          destinationIndex: res?.destination.index,
        };
        handleUpdateKanban(data);
      } else {
        if (res?.destination?.droppableId) {
          let selectedStatus = statuses.find(
            (val) => val._id === res?.destination?.droppableId
          ) || { key: "" };
          // Update BOTH redux slices the Kanban renders from:
          //   - state.post.posts   → PostsPagePersonalArea (single brand)
          //   - state.user.allPosts → AllPostsPage (cross-brand)
          // and the brand.posts entry inside state.user.brands so the
          // card-list view stays consistent too.
          const queueStatus = deriveQueueStatus(selectedStatus?.key);
          dispatch(
            updatePostStatus({
              postId: res.draggableId,
              statusId: selectedStatus?._id ?? "",
              queueStatus,
            })
          );
          dispatch(
            updateAllPostsStatus({
              postId: res.draggableId,
              statusId: selectedStatus?._id ?? "",
              queueStatus,
            })
          );

          // Send BOTH the column id (status) and the derived queueStatus
          // enum to the backend so it can fire the POST_APPROVED outbox
          // event when the user drags into Approved.
          const body = { status: selectedStatus._id };
          if (queueStatus) body.queueStatus = queueStatus;
          updatePostStatusAPI({ body, id: res.draggableId });
        }
      }
    } else {
      const newStatusId = res?.destination.droppableId;
      const postToUpdate = res?.draggableId;
      const destStatus = statuses?.find((s) => s._id === newStatusId) || { key: "" };
      const queueStatus = deriveQueueStatus(destStatus?.key);
      // IMMUTABLE update — was mutating x.status in-place which left
      // the same object ref in every clone of posts; bucketForItem
      // then read the OLD queueStatus from the still-shared object
      // and routed the dragged idea back to the original column.
      const updatedPosts = (posts || []).map((x) =>
        x && x._id === postToUpdate
          ? {
              ...x,
              status: newStatusId,
              ...(queueStatus ? { queueStatus } : {}),
            }
          : x
      );
      dispatch(setIdeas(updatedPosts));

      const data = { status: newStatusId, _id: postToUpdate };
      if (queueStatus) data.queueStatus = queueStatus;
      updateIdeaStatusAPI(data);
    }
  }

  const { loading, request: updatePostStatusAPI } = useHttp({
    requestCallback: (data) => editPost(data),
    onLoad: (res) => {
      addNotification("Post updated successfully", "success");
    },
    onError: (res) => {
      addNotification(
        res.err ?? "Something went wrong please connect with support",
        "danger"
      );
    },
  });

  const { loading: ideaLoading, request: updateIdeaStatusAPI } = useHttp({
    requestCallback: (data) => updateIdea(data),
    onLoad: (res) => {
      addNotification("Idea status updated successfully", "success");
    },
    onError: (res) => {
      addNotification(
        res.err ?? "Something went wrong please connect with support",
        "danger"
      );
    },
  });

  const rearrangeMilestone = () => {
    if (statuses) {
      const index = statuses?.findIndex(
        (obj) => obj.key.toLowerCase() === "pending_idea"
      );

      if (index !== -1) {
        const removedObject = statuses?.splice(index, 1)[0];
        statuses.unshift(removedObject);
      }
    }
  };

  useEffect(() => {
    rearrangeMilestone();
  }, []);

  const { loading: milestoneLoading, request: handleUpdateKanban } = useHttp({
    requestCallback: updateKanban,
    onLoad: (res) => {
      console.log(res);
      if (res?.success) {
        dispatch(setMilestones(res.data));
      }
      addNotification("Status updated successfully", "success");
    },
    onError: (res) => {
      addNotification(
        res.err ?? "Something went wrong please connect with support",
        "danger"
      );
    },
  });

  const sortedStatuses =
    statuses?.sort(
      (a, b) => (a.statusIndex || 0) - (b.statusIndex || 0)
    ) ?? [];

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="all-columns" direction="horizontal" type="column">
        {(provided) => (
          <div
            {...provided.droppableProps}
            ref={provided.innerRef}
            className="d-flex karban-container"
          >
            {statuses &&
              sortedStatuses.map((mile, i) => (
                <KanbanCategory
                  // placeholderProps={placeholderProps}
                  color={colors[i]}
                  onEdit={onEdit}
                  onArchive={onArchive}
                  source={source}
                  index={i}
                  key={mile._id}
                  mile={mile}
                  projects={posts}
                  setModal={setModal}
                  setBrandToDelete={setBrandToDelete}
                  setBrandToUpdate={setBrandToUpdate}
                  setBrandListToEdit={setBrandListToEdit}
                />
              ))}
            {provided.placeholder}
            {/* </MDBContainer> */}
          </div>
        )}
      </Droppable>
    </DragDropContext>
  );
};

export default KanbanBoard;
